import React, { useEffect, useMemo, useRef, useState } from "react";
import BackButton from "../BackButton/BackButton";
import DataTableListing from "../DataTableListing/DataTableListing";
import trash from "../../assets/images/delete.svg";
import search_bar from "../../assets/images/search.svg";
import sort from "../../assets/images/sort.png";
import "./LearningSpecialistStudents.css";
import AccessRoleModal from "../AccessRoleModal/AccessRoleModal";
import { useDispatch, useSelector } from "react-redux";
import { getAccessControlRole, resetAccessControlRoleList } from "../../state/actions/getAccessControlRoleAction";
import { useAuth } from "../../features/auth/AuthContext";
import { getTeacherAndEnrolled, resetTeacherAndEnrolledList } from "../../state/actions/getTeacherAndEnrolledListAction";
import ConfirmDeleteModal from "../AccessRoleModal/ConfirmDeleteModal";
import api from "../../api/api"
import { useNavigate } from "react-router";
import EnrollmentResponsiveCards from "../../modules/admin/Dashboard/RecentEnrollment/EnrollmentResponsiveCards";
import SearchComponent from "../SearchComponent/SearchComponent";

const BASE_GRADE_OPTIONS = [
  { label: "Pre-Kindergarten", value: "1" },
  { label: "Kindergarten", value: "2" },
  { label: "Grade 1", value: "3" },
  { label: "Grade 2", value: "4" },
  { label: "Grade 3", value: "5" },
  { label: "Grade 4", value: "6" },
  { label: "Grade 5", value: "7" },
  { label: "Grade 6", value: "8" },
  { label: "Grade 7", value: "9" },
  { label: "Grade 8", value: "10" },
  { label: "Grade 9", value: "11" },
  { label: "Grade 10", value: "12" },
  { label: "Grade 11", value: "13" },
  { label: "Grade 12", value: "14" },
];

const PROSCHOOL_EXTRA_GRADES = [
  { label: "Undergraduate", value: "15" },
  { label: "Graduate", value: "16" },
  { label: "Masters", value: "17" },
  { label: "Doctorate", value: "18" },
  { label: "Post Doctorate", value: "19" },
  { label: "Corporate", value: "20" },
];

const buildColumns = (onDelete, allowDelete, onStudentViewProfile, formatDate, activeTab) => [
  {
    key: "student_name",
    title: "Student Name",
    render: (value, row) => (
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {row.student_profile_pic ? (
          <img
            src={row.student_profile_pic}
            alt={value}
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              objectFit: "cover",
              flexShrink: 0,
            }}
          />
        ) : (
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "#e2e8f0",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              color: "#94a3b8",
              fontWeight: 600,
            }}
          >
            {value?.charAt(0)}
          </div>
        )}
        <span>{value}</span>
      </div>
    ),
  },
  {
    key: "student_id",
    title: "Student Id",
    headerStyle: { textAlign: "center" },
      render: (value) => (
        <div style={{ textAlign: "center" }}>
          {value}
        </div>
      ),
  },
  { 
    key: "grade_name", 
    title: "Grade",
    headerStyle: { textAlign: "center" },
      render: (value) => (
        <div style={{ textAlign: "center" }}>
          {value}
        </div>
      ),
   },
  {
    key: "email",
    title: "Email Id"
  },
  {
    key: activeTab === "assigned" ? "assigned_date" : "students_join_date",
    title: activeTab === "assigned" ? "Assigned On" : "Joined On",
    headerStyle: { textAlign: "center" },
      render: (value) => (
        <div style={{ textAlign: "center" }}>
          {formatDate(value)}
        </div>
      ),
  },
  {
    key: "action",
    title: "Action",
    headerStyle: { textAlign: "center" },
    render: (_, row) => (
      <div style={{ display: "flex", alignItems: "center",justifyContent:"center", gap: 12 }}>
        <span
          style={{
            color: "#0068FF",
            fontSize: 13.5,
            fontWeight: 500,
            cursor: "pointer",
          }}
          onClick={(e) => {
            e.stopPropagation();
            onStudentViewProfile(row.student_id)
          }}
        >
          View Profile
        </span>
        {allowDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(row);
          }}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 4,
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
          }}
        >
          <img width="14" height="15" src={trash} alt="trash" />
        </button>
        )}
      </div>
    ),
  },
];

const LearningSpecialistStudents = ({ navigateBack, specialist }) => {
  const [activeTab, setActiveTab] = useState("assigned"); // "assigned" | "advisor"
  const [searchText, setSearchText] = useState("");
  const dispatch = useDispatch();
  const {token} = useAuth();
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [sortValue, setSortValue] = useState("");
  const sortRef = useRef(null);
  const navigate = useNavigate();

  const specialistId     = specialist?.specialist_id;
  const specialistName   = specialist?.specialist_name  ?? "Learning Specialist";
  const specialistAvatar = specialist?.ai_avatar        ?? null;
  
  const sortOptions = [
    { label: "Name : A - Z", value: "Name:A-Z" },
    { label: "Name : Z - A", value: "Name:Z-A" },
    { label: "Newest First", value: "Newest" },
    { label: "Oldest First", value: "Oldest-First" },
    { label: "Grade High to Low", value: "htl" },
    { label: "Grade Low to High", value: "lth" },
  ];
  // ── Add these states ──
  const [showGradeMenu, setShowGradeMenu] = useState(false);
  const [gradeValue, setGradeValue] = useState("");
  const gradeRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const globalState = useSelector((state) => state.globalState);
  const isMarketplace = globalState?.enabled_modules?.includes("marketplace");

  const gradeOptions = useMemo(
    () =>
      isMarketplace
        ? [...BASE_GRADE_OPTIONS, ...PROSCHOOL_EXTRA_GRADES]
        : BASE_GRADE_OPTIONS,
    [isMarketplace]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [sortValue, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 400);
    return () => clearTimeout(timer);
  }, [searchText]);

 // ── Combine both in one useEffect ──
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setShowSortMenu(false);
      }
      if (gradeRef.current && !gradeRef.current.contains(e.target)) {
        setShowGradeMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
      // Update fetch to include sort

  useEffect(() => {
 if (activeTab === "assigned") {
    dispatch(
      getAccessControlRole(
        token,
        "ls",
        "assigned",
        specialistId,
        sortValue || undefined,
        gradeValue || undefined,
        debouncedSearch || undefined,
        currentPage
      )
    );
  } else {
    dispatch(
      getTeacherAndEnrolled(
        token,
        "enrolled_students",
        specialistId,
        sortValue || undefined,
        gradeValue || undefined,
        debouncedSearch || undefined,
        undefined, // school_id
        undefined, // subject_id
        undefined, // role
        currentPage
      )
    );
  }
}, [specialistId, activeTab, sortValue, gradeValue, debouncedSearch, currentPage]);

  const {roleList, isLoading, count} = useSelector(
    (state) => state.getAccessControlRoleList
  );

  const { teacherList: enrolledStudents, isLoading: enrolledLoading,  count: enrolledCount } = useSelector(
  (state) => state.getTeacherAndEnrolled
  );

  const learningSpecialistStudentsData = enrolledStudents ?? [];

  const assignedStudentsData = roleList?? [];

  // this helps for to show count of both tabs simultaneously
   useEffect(() => {
    if (!specialistId) return;

    dispatch(resetAccessControlRoleList()); // reset first, clears stale data immediately 
    // dispatch(getAccessControlRole(token, "ls", "assigned", specialistId));

     if (specialistId) {
      dispatch(getTeacherAndEnrolled(token, "enrolled_students", specialistId,
      undefined,  // sort
      undefined,  // grade
      undefined,  // search
      undefined,  // school_id
      undefined,  // subject_id
      undefined,  // role
      1,
    ));
    }
    return () => {
      dispatch(resetAccessControlRoleList());
      dispatch(resetTeacherAndEnrolledList());
    };
  }, [specialistId]);

  const handleModalClose = () => {
  setOpen(false);
  if (specialistId) {
    dispatch(getAccessControlRole(token, "ls", "assigned", specialistId));
  }
};

  const sourceData =
    activeTab === "assigned" ? assignedStudentsData : learningSpecialistStudentsData;

  // const filteredData = sourceData.filter((s) => {
  //   const matchSearch =
  //     s.student_name?.toLowerCase().includes(searchText.toLowerCase()) ||
  //     s.email?.toLowerCase().includes(searchText.toLowerCase());
  //   return matchSearch;
  // });

  const filteredData = sourceData;

  const [open, setOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, row: null });
  
  const handleDelete = (row) => setDeleteModal({ open: true, row });
  
  // Add delete handler
  const confirmDelete = async () => {
    const row = deleteModal.row;
    setDeleteModal({ open: false, row: null });
  
    try {
      const result = await api.delete("adminpage/access-control/delete/", {
        headers: { "Content-Type": "application/json", Authorization: `Token ${token}` },
        data: {
          type: "assigned_student", 
          student_id: row.student_id,
          teacher_id: specialistId,
          role: "ls",
        },
      });
      if (result.data?.status === 200) {
        dispatch(getAccessControlRole(token, "ls", "assigned", specialistId)); // re-fetch
      } else {
        console.error("Delete failed:", result.data?.message);
      }
    } catch (error) {
      console.error("Delete error:", error.response?.data ?? error.message);
    }
  };

  // ── Reset on tab switch ──
const handleTabChange = (tab) => {
  setActiveTab(tab);
  setSortValue("");
  setGradeValue("");
  setSearchText("");
  setDebouncedSearch("");
  setCurrentPage(1); 
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  const day = date.toLocaleString("en-GB", { day: "2-digit" });
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

 const onTeacherViewProfile = (specialistId) => {
    navigate(`/TeacherProfile/${specialistId}`, {
      state: {
        from: "/dashboard/learning-specialist",
        openViewStudents: true,
        ls: specialist
      }
    });
  };

const onStudentViewProfile = (student_id) => {
  navigate(`/student/${student_id}`, {
    state: {
      teacher_id: specialistId,
      from: "/dashboard/learning-specialist",
      openViewStudents: true,
      ls: specialist  
    }
  });
};

  const totalCount =
  activeTab === "assigned"
    ? (count ?? 0)
    : (enrolledCount ?? 0);

  return (
    <>
    <div className="specialist-students-page">
      <BackButton buttonName="Back" handleClick={navigateBack} />

      <h2 className="page-title">Learning Specialist Overview</h2>

      {/* Advisor Card */}
      <div className="specialist-card">
        <img
          src={specialistAvatar}
          alt={specialistName}
          className="specialist-avatar"
        />
        <p className="specialist-name">{specialistName}</p>
        <p className="specialist-role">Learning Specialist</p>
        <span onClick={() => {
            onTeacherViewProfile(specialistId);
          }}
          className="view-profile-link"
        >
          View Profile
        </span>
      </div>

      {/* Tabs */}
      <div className="students-tabs">
        <button
          className={`tab-btn ${activeTab === "assigned" ? "active" : ""}`}
          onClick={() => handleTabChange("assigned")}
        >
          Assigned Students (
          {String(count ?? 0).padStart(2, "0")})
        </button>
        <button
          className={`tab-btn ${activeTab === "advisor" ? "active" : ""}`}
          onClick={() => handleTabChange("advisor")}
        >
          Learning Specialist Students (
          {String(enrolledCount ?? 0).padStart(2, "0")})
        </button>
      </div>

      {/* Toolbar */}
      <div className="students-toolbar">
        <div className="toolbar-left">
          {/* Search */}
          <SearchComponent
            search={search_bar}
            searchPlaceholder="Search Students"
            value={searchText}
            onchange={(e) => setSearchText(e.target.value)} 
          />
       {/*    <div className="search-bar">
            <img src={search_bar} alt="search" />
             <div className="search-divider"></div>
            <input
              placeholder="Search Students"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div> */}

        </div>
        <div className="specialist-toolbar-right">
          {/* Grade dropdown */}
            <div style={{ position: "relative" }} ref={gradeRef}>
              <button
                className="grade-dropdown !border-none md:border"
                onClick={() => setShowGradeMenu((prev) => !prev)}
              >
                <span>
                  {gradeValue
                    ? gradeOptions.find((o) => o.value === gradeValue)?.label ??
                      "Select Grade"
                    : "Select Grade"}
                </span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2 4L6 8L10 4"
                    stroke="#64748b"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {showGradeMenu && (
                <div className="sort-menu" style={{ minWidth: 170, left: 0 }}>
                  {/* Clear option */}
                  <div
                    className="sort-options"
                    onClick={() => {
                      setGradeValue("");
                      setShowGradeMenu(false);
                    }}
                    style={{
                      color: !gradeValue ? "#0068FF" : "#374151",
                      fontWeight: !gradeValue ? 600 : 400,
                    }}
                  >
                    All Grades
                  </div>
                  {gradeOptions.map((option) => (
                    <div
                      className="sort-options"
                      key={option.value}
                      onClick={() => {
                        setGradeValue(option.value);
                        setShowGradeMenu(false);
                      }}
                      style={{
                        color:
                          gradeValue === option.value ? "#0068FF" : "#374151",
                        fontWeight: gradeValue === option.value ? 600 : 400,
                        background:
                          gradeValue === option.value ? "#f0f7ff" : "#fff",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f9fafb")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background =
                          gradeValue === option.value ? "#f0f7ff" : "#fff")
                      }
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          <div className="specialist-sort-button" style={{ position: "relative" }} ref={sortRef}>
            <button className="sort-button" onClick={() => setShowSortMenu((prev) => !prev)}>
              <img src={sort} alt="sort" />
              Sort
            </button>
          
            {showSortMenu && (
              <div className="sort-menu">
                {sortOptions.map((option) => (
                  <div className="sort-options"
                    key={option.value}
                    onClick={() => {
                      setSortValue(option.value);
                      setShowSortMenu(false);
                    }}
                    style={{
                      color: sortValue === option.value ? "#0068FF" : "#374151",
                      fontWeight: sortValue === option.value ? 600 : 400,
                      background: sortValue === option.value ? "#f0f7ff" : "#fff",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f9fafb"}
                    onMouseLeave={(e) => e.currentTarget.style.background = sortValue === option.value ? "#f0f7ff" : "#fff"}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          {activeTab==="assigned" && (
          <button
            className="assign-students-button"
            onClick={() => setOpen(true)}
          >
            Assign Students
          </button>
           )}
        </div>
      </div>

      {/* Table */}
      <div>
       {isMobile ? (
            <EnrollmentResponsiveCards
              data={filteredData.map((item) => ({
                ...item,
                profilePic: item.student_profile_pic,
                student_name: item.student_name,
                student_id: item.student_id,
                grade: item.grade_name,
                email: item.email,
                joinDate: formatDate(item.students_join_date) || formatDate(item.assigned_date),
                id: item.student_id,
              }))}
              userType={"student"}
              isAssignedTab={activeTab === "assigned"}
              loading={isLoading}
              onCardClick={(item) => onStudentViewProfile(item.student_id)}
              showActions={false}
                            emptyText={
                searchText
                  ? "No Students found"
                  : activeTab === "assigned"
                  ? "No students Assigned yet"
                  : "No students enrolled yet"
              }
              LineTwoText={
                searchText
                  ? ""
                  : activeTab === "assigned"
                  ? "Once Students are assigned to this Learning Specialist, they'll appear here"
                  : "Once Students are enrolled, they'll appear here"
              }
            />
          ) : (
            <DataTableListing
              columns={buildColumns(
                handleDelete,
                activeTab === "assigned",
                onStudentViewProfile,
                formatDate,
                activeTab,
              )}
              dataSource={filteredData}
              rowKey="student_id"
              pagination={
                totalCount > 12
                  ? {
                      currentPage: currentPage,
                      total: totalCount,
                      pageSize: 12,
                      onChange: (page) => setCurrentPage(page),
                    }
                  : false
              }
              checkable={false}
              loading={isLoading}
              emptyText={
                searchText
                  ? "No Students found"
                  : activeTab === "assigned"
                  ? "No students Assigned yet"
                  : "No students enrolled yet"
              }
              LineTwoText={
                searchText
                  ? ""
                  : activeTab === "assigned"
                  ? "Once Students are assigned to this Learning Specialist, they'll appear here"
                  : "Once Students are enrolled, they'll appear here"
              }
              bordered={false}
              size="default"
              rowHoverColor="#F7F7F7"
            />
          )}
      </div>
    </div>

     <AccessRoleModal open={open} onClose={handleModalClose}  mode="student" teacher_id={specialistId} role="ls"
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      >
      </AccessRoleModal>

     <ConfirmDeleteModal
       open={deleteModal.open}
       onClose={() => setDeleteModal({ open: false, row: null })}
       onConfirm={confirmDelete}
       name={deleteModal.row?.student_name}
       role="ls_student"
     />     
      </>
  );
};

export default LearningSpecialistStudents;